import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";
import { SITE } from "../data/siteData";
import { curriculum } from "../data/curriculum";

export default function Navbar() {
  const { currentUser, logout, hasFullAccess } = useAuth();
  const { totalComplete } = useProgress();
  const navigate = useNavigate();
  const [userMenu, setUserMenu] = useState(false);
  const ref = useRef(null);

  const total = curriculum.flatMap((w) => w.lessons).length;
  const pct = total > 0 ? Math.round((totalComplete / total) * 100) : 0;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      ref={ref}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        fontFamily: "'Nunito', 'Segoe UI', Arial, sans-serif",
      }}
    >
      {/* Dark top bar only — no ribbon */}
      <div
        style={{
          background: "#282a35",
          height: 46,
          display: "flex",
          alignItems: "center",
          padding: "0 16px",
          gap: 16,
        }}
      >
        <span
          onClick={() => navigate("/")}
          style={{
            fontWeight: 900,
            fontSize: 19,
            color: "#fff",
            cursor: "pointer",
            letterSpacing: -0.3,
            flexShrink: 0,
          }}
        >
          {SITE.name}
        </span>

        {currentUser && totalComplete > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 72,
                height: 4,
                background: "rgba(255,255,255,0.15)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: "#009dc4",
                  borderRadius: 2,
                  transition: "width 0.3s",
                }}
              />
            </div>
            <span style={{ color: "#aaa", fontSize: 11 }}>{pct}%</span>
          </div>
        )}

        <div style={{ flexGrow: 1 }} />

        {currentUser ? (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setUserMenu((v) => !v)}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 4,
                color: "#fff",
                fontSize: 13,
                padding: "4px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontFamily: "inherit",
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "#009dc4",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {currentUser.name[0].toUpperCase()}
              </span>
              {currentUser.name.split(" ")[0]}
              <span style={{ fontSize: 9, color: "#aaa" }}>▾</span>
            </button>
            {userMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                  minWidth: 210,
                  zIndex: 200,
                  padding: "6px 0",
                }}
              >
                <div
                  style={{
                    padding: "8px 14px 10px",
                    borderBottom: "1px solid #f0f0f0",
                    background: "#fafafa",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a1a" }}>
                    {currentUser.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#777", marginTop: 1 }}>
                    {currentUser.email}
                  </div>
                  {currentUser.referral_code && (
                    <div style={{ fontSize: 11, color: "#777", marginTop: 3 }}>
                      Referral:{" "}
                      <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#009dc4" }}>
                        {currentUser.referral_code}
                      </span>
                    </div>
                  )}
                  {hasFullAccess && (
                    <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700, marginTop: 4 }}>
                      ✓ Full access unlocked
                    </div>
                  )}
                </div>
                {!hasFullAccess && (
                  <DDItem onClick={() => { setUserMenu(false); navigate("/unlock"); }}>
                    🔓 Enter access code
                  </DDItem>
                )}
                <DDItem onClick={() => { setUserMenu(false); navigate("/course"); }}>
                  📚 My course
                </DDItem>
                <DDItem
                  onClick={() => { setUserMenu(false); logout(); navigate("/"); }}
                  style={{ color: "#dc2626", borderTop: "1px solid #f0f0f0" }}
                >
                  Sign out
                </DDItem>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => navigate("/auth")}
              style={{
                background: "none",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 4,
                color: "#ddd",
                fontSize: 13,
                padding: "4px 14px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Sign in
            </button>
            <button
              onClick={() => navigate("/auth?mode=register")}
              style={{
                background: "#009dc4",
                border: "none",
                borderRadius: 4,
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                padding: "4px 14px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Get started
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DDItem({ children, onClick, style: s }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: "none",
        border: "none",
        padding: "9px 14px",
        fontSize: 13,
        color: "#282a35",
        cursor: "pointer",
        fontFamily: "inherit",
        ...(s || {}),
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
    >
      {children}
    </button>
  );
}